import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';

import LoginLayout from 'containers/login/login-layout';

import { Stack } from 'ui/stack';

import { Typography } from 'ui/typography';
import { Loader } from 'ui/loader';
import { Input } from 'ui/input';
import Button from 'components/partials/button/button';
import toast from 'components/partials/toast/toast';

import type { ApiErrorWithMeta } from 'api/interceptors';

import {
  submitUserAccountActivation,
  validateUserActivationKey,
} from 'hooks/public-account-requests';
import usePageTitle from 'hooks/use-page-title';

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    .min(14, 'Password must be at least 14 characters')
    .matches(/[a-z]/, 'Password must contain a lowercase letter')
    .matches(/[A-Z]/, 'Password must contain an uppercase letter')
    .matches(/[0-9]/, 'Password must contain a number')
    .matches(/[#?!@$%^&*-]/, 'Password must contain a symbol (#?!@$%^&*-)')
    .test(
      'no-repeating-characters',
      'Password must not repeat the same character more than 4 times in a row',
      (value) => !value || !/(.)\1{4,}/.test(value)
    ),
  passwordConfirmation: Yup.string()
    .oneOf(
      [Yup.ref('password'), null] as unknown as string[],
      'Must match password'
    )
    .required('Password confirmation is required'),
});

type EnrollFormValues = Yup.InferType<typeof validationSchema>;

const EnrollAccount = (): React.JSX.Element => {
  usePageTitle('Activate Account');

  const [valid, setValid] = useState<boolean | null>(null);
  const { activationKey, oktaUserId } = useParams<{
    activationKey: string;
    oktaUserId: string;
  }>();
  const navigate = useNavigate();

  useEffect(() => {
    validateUserActivationKey(activationKey as string)
      .then(() => setValid(true))
      .catch(() => {
        toast.error({
          title: 'Error activating user',
          message: `We couldn't find a user matching that activation key (${activationKey}). Please try again or contact support.`,
        });
        setValid(false);
      });
  }, [activationKey]);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<EnrollFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      password: '',
      passwordConfirmation: '',
    },
  });

  const onSubmit = async (values: EnrollFormValues): Promise<void> => {
    try {
      await submitUserAccountActivation({
        activationKey: activationKey as string,
        oktaUserId: oktaUserId as string,
        password: values.password,
      });

      toast.success({
        title: 'Success!',
        message: 'Your account has been activated. Please login to continue.',
      });

      navigate('/login');
    } catch (error) {
      const apiError = error as ApiErrorWithMeta;

      const message =
        apiError?.data?.detail ||
        'There was an error when activating your account. Please try again or contact support.';

      toast.error({
        title: 'Activation error',
        message,
      });
    }
  };

  return (
    <LoginLayout>
      <form onSubmit={handleSubmit(onSubmit)}>
        {valid !== false && (
          <>
            {valid === null ? (
              <Loader loading={true} />
            ) : (
              <>
                <Typography
                  data-testid="enroll-account-heading"
                  variant="h1"
                  component="h2"
                  sx={(theme) => ({
                    mb: 2,
                    textAlign: 'center',
                    [theme.breakpoints.down('tablet')]: {
                      fontSize: theme.typography.h3.fontSize,
                      lineHeight: theme.typography.h3.lineHeight,
                    },
                  })}
                >
                  Choose a password
                </Typography>
                <Controller
                  name="password"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      type="password"
                      label="Password"
                      placeholder="Enter your password"
                      error={!!error}
                      errorMessage={error?.message}
                      sx={{ mt: 1.5 }}
                      helperText="Must be at least 14 characters long and include a lowercase letter, an uppercase letter, a number, and a symbol (#?!@$%^&*-). No character may repeat more than 4 times in a row. Can't reuse any of your last 14 passwords."
                    />
                  )}
                />
                <Controller
                  name="passwordConfirmation"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      type="password"
                      label="Confirm Password"
                      placeholder="Confirm your password"
                      error={!!error}
                      errorMessage={error?.message}
                      sx={{ mt: 1.5 }}
                    />
                  )}
                />
                <Stack
                  direction="row"
                  sx={{ justifyContent: 'flex-end', mt: 2 }}
                >
                  <Button
                    type="submit"
                    variant="brand-filled"
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    {isSubmitting ? 'Submitting...' : 'Continue'}
                  </Button>
                </Stack>
              </>
            )}
          </>
        )}
      </form>
    </LoginLayout>
  );
};

export default EnrollAccount;
